import { useNavigate } from '@tanstack/react-router';
import { Group, Stack, Text } from '@mantine/core';
import { Monitor, Terminal } from 'lucide-react';
import { Button } from '../ui';
import type { RangeHealthVm } from '../../api/types';

/**
 * RangeConsoleView — trainee console launcher.
 *
 * Operator design (2026-06-04): consoles open as a full-screen, ludus2-style
 * page (`/console/$rangeId/$vmName`) instead of a cramped embedded pane. This
 * component lists the trainee's assigned workstation(s); clicking one navigates
 * to the immersive FullPageConsole (toolbar + full-bleed iframe + Range Info
 * overlay). The token grant ensures the SSM tunnel server-side (Phase B), so
 * the desktop actually paints.
 */
export interface RangeConsoleViewProps {
  rangeId: string;
  assignedVms: ReadonlyArray<RangeHealthVm>;
}

export function RangeConsoleView({ rangeId, assignedVms }: RangeConsoleViewProps) {
  const navigate = useNavigate();
  const open = (vmName: string) =>
    navigate({ to: '/console/$rangeId/$vmName', params: { rangeId, vmName } });

  if (assignedVms.length === 0) {
    return (
      <Stack gap="sm" align="center" py="lg">
        <Text size="sm" c="dimmed">
          No workstations are assigned to you in this range.
        </Text>
      </Stack>
    );
  }

  return (
    <Stack gap="sm">
      <Group gap={6}>
        <Monitor size={14} color="var(--mantine-color-dimmed)" />
        <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
          {assignedVms.length > 1 ? 'My workstations' : 'My workstation'}
        </Text>
      </Group>
      <Group gap="sm" wrap="wrap">
        {assignedVms.map((vm) => {
          const name = vm.vmName || vm.hostname;
          return (
            <Button
              key={vm.instanceId}
              variant="primary"
              size="sm"
              leftIcon={<Terminal size={14} />}
              disabled={vm.state !== 'running'}
              onClick={() => open(name)}
            >
              Open console — {name}
            </Button>
          );
        })}
      </Group>
    </Stack>
  );
}
